import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import SignaturePad from 'signature_pad';
import { ReactComponent as DigntecLogo } from '../../Assets/images/DigntecLogo.svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './contractSign.css';

function ContractSign() {
  const { userId, token } = useParams();
  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [userData, setUserData] = useState(null);
  const [signeeName, setSigneeName] = useState('');
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [shippingSame, setShippingSame] = useState(true);
  const [shippingAddress, setShippingAddress] = useState({ street: '', street2: '', city: '', state: '', zipcode: '', recipientName: '', recipientPhone: '' });
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [wantsResign, setWantsResign] = useState(false);

  useEffect(() => {
    api.get(`/contract/verify/${userId}/${token}`)
      .then(res => {
        setUserData(res.data);
        setSigneeName(res.data.username || '');
        if (res.data.contractStatus === 'signed') setAlreadySigned(true);
        setLoading(false);
      })
      .catch(() => { setExpired(true); setLoading(false); });
  }, [userId, token]);

  // signature_pad 초기화 + 리사이즈 대응
  const initSigPad = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    canvas.getContext('2d').scale(ratio, ratio);

    if (sigPadRef.current) sigPadRef.current.off();
    sigPadRef.current = new SignaturePad(canvas, {
      minWidth: 1.5,
      maxWidth: 3.5,
      penColor: '#1a1a2e',
      backgroundColor: 'rgba(0,0,0,0)',
      velocityFilterWeight: 0.7,
    });
  }, []);

  useEffect(() => {
    if (!loading && !expired && !signed) {
      // DOM 렌더 후 초기화
      const timer = setTimeout(initSigPad, 100);
      window.addEventListener('resize', initSigPad);
      return () => { clearTimeout(timer); window.removeEventListener('resize', initSigPad); };
    }
  }, [loading, expired, signed, initSigPad]);

  const clearSignature = () => {
    if (sigPadRef.current) sigPadRef.current.clear();
  };

  const undoStroke = () => {
    if (!sigPadRef.current) return;
    const data = sigPadRef.current.toData();
    if (data.length > 0) {
      data.pop();
      sigPadRef.current.fromData(data);
    }
  };

  const handleSubmit = async () => {
    if (!signeeName.trim()) return toast.error('Please enter your name');
    if (!agreedTerms) return toast.error('Please agree to the terms');
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) return toast.error('Please sign above');

    const signatureData = sigPadRef.current.toDataURL('image/png');

    setSubmitting(true);
    try {
      await api.post(`/contract/sign/${userId}/${token}`, {
        signeeName: signeeName.trim(),
        agreedTerms: true,
        signatureImage: signatureData,
        shippingAddress: shippingSame ? { sameAsStore: true } : { sameAsStore: false, ...shippingAddress },
      });
      setSigned(true);
      toast.success('Contract signed successfully!');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to submit');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="cs-root"><div className="cs-loading">Loading...</div></div>;
  if (expired) return (
    <div className="cs-root">
      <div className="cs-card" style={{ textAlign: 'center', padding: 40 }}>
        <h2>Link Expired</h2>
        <p>This contract signing link has expired or is invalid.</p>
        <p>Please contact <a href="mailto:support@digntec.com">support@digntec.com</a></p>
      </div>
    </div>
  );
  if (signed) return (
    <div className="cs-root">
      <div className="cs-card" style={{ textAlign: 'center', padding: 40 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
        <h2>Contract Signed!</h2>
        <p>Thank you, <strong>{signeeName}</strong>.</p>
        <p>Your contract has been submitted. We will begin preparing your devices.</p>
        <p style={{ color: '#888', fontSize: 14, marginTop: 16 }}>You can now close this page.</p>
      </div>
    </div>
  );

  if (alreadySigned && !wantsResign) return (
    <div className="cs-root">
      <div className="cs-card" style={{ textAlign: 'center', padding: 40 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: '#551FFF', mb: 2 }} />
        <h2>Contract Already Signed</h2>
        <p>This contract for <strong>{userData?.storeName || userData?.username}</strong> was signed
          {userData?.contractSigneeName ? ` by ${userData.contractSigneeName}` : ''}
          {userData?.contractSignedAt ? ` on ${new Date(userData.contractSignedAt).toLocaleDateString('en-US')}` : ''}.
        </p>
        {userData?.contractDocUrl && (
          <p><a href={userData.contractDocUrl} target="_blank" rel="noreferrer"
            style={{ color: '#551FFF', fontWeight: 600 }}>View Signed Contract</a></p>
        )}
        <p style={{ color: '#888', fontSize: 14, marginTop: 16 }}>
          If you need to re-sign, click below.
        </p>
        <button className="cs-submit" style={{ marginTop: 12, background: '#888' }}
          onClick={() => setWantsResign(true)}>
          Re-sign Contract
        </button>
        <p className="cs-footer" style={{ marginTop: 20 }}>
          Digntec LLC · 15 Veterans Way, Edgewater, NJ 07020 · support@digntec.com
        </p>
      </div>
    </div>
  );

  return (
    <div className="cs-root">
      <div className="cs-card">
        {/* Header */}
        <div className="cs-header">
          <DigntecLogo style={{ width: 120, height: 'auto' }} />
          <h2>Digital Signage Agreement</h2>
        </div>

        {/* 드라이 테스트 링크 — 미리보기만, 서명은 서버가 막는다 (리뷰 M1) */}
        {userData?.dryTest && (
          <div style={{ background: '#FEF3C7', color: '#92400E', padding: '10px 14px', borderRadius: 8, margin: '12px 0', fontSize: 14 }}>
            Dry test preview — nothing is saved and this link cannot be signed.
          </div>
        )}

        {/* Store Info */}
        <div className="cs-info">
          <p><strong>{userData?.storeName || userData?.username}</strong></p>
          <p>{userData?.town}, {userData?.state} {userData?.zipcode}</p>
        </div>

        {/* Contract Summary */}
        <div className="cs-terms">
          <h3>Contract Terms</h3>
          <ul>
            <li>Menu board digital signage service for your store</li>
            <li>Initial setup fee: <strong>$300</strong></li>
            <li>Monthly service fee: <strong>$75</strong> (ACH)</li>
            <li>Includes menu price updates, seasonal updates, and device support</li>
            <li>Auto-renewal annually; 30 days notice for termination</li>
            <li>HQ may request pricing and seasonal menu changes</li>
          </ul>
          {userData?.contractDocUrl && (
            <p className="cs-terms-link">
              <a href={userData.contractDocUrl} target="_blank" rel="noreferrer">View full contract PDF</a>
            </p>
          )}
        </div>

        {/* Shipping Address */}
        <div className="cs-section">
          <h3>Device Shipping Address</h3>
          <div className="cs-radio-group">
            <label className="cs-radio">
              <input type="radio" checked={shippingSame} onChange={() => setShippingSame(true)} />
              Same as store address
            </label>
            <label className="cs-radio">
              <input type="radio" checked={!shippingSame} onChange={() => setShippingSame(false)} />
              Different address
            </label>
          </div>
          {!shippingSame && (
            <div className="cs-shipping-form">
              <input placeholder="Street Address" value={shippingAddress.street}
                onChange={e => setShippingAddress(a => ({ ...a, street: e.target.value }))} />
              <input placeholder="Apt, Suite, Unit, etc. (optional)" value={shippingAddress.street2}
                onChange={e => setShippingAddress(a => ({ ...a, street2: e.target.value }))} />
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="City" value={shippingAddress.city} style={{ flex: 1 }}
                  onChange={e => setShippingAddress(a => ({ ...a, city: e.target.value }))} />
                <input placeholder="State" value={shippingAddress.state} style={{ width: 60 }}
                  onChange={e => setShippingAddress(a => ({ ...a, state: e.target.value }))} />
                <input placeholder="Zip" value={shippingAddress.zipcode} style={{ width: 80 }}
                  onChange={e => setShippingAddress(a => ({ ...a, zipcode: e.target.value }))} />
              </div>
              <input placeholder="Recipient Name" value={shippingAddress.recipientName}
                onChange={e => setShippingAddress(a => ({ ...a, recipientName: e.target.value }))} />
              <input placeholder="Phone" value={shippingAddress.recipientPhone}
                onChange={e => setShippingAddress(a => ({ ...a, recipientPhone: e.target.value }))} />
            </div>
          )}
        </div>

        {/* Signee Name */}
        <div className="cs-section">
          <h3>Your Name</h3>
          <input className="cs-name-input" value={signeeName}
            onChange={e => setSigneeName(e.target.value)}
            placeholder="Full Name" />
        </div>

        {/* Signature Pad */}
        <div className="cs-section">
          <h3>Signature</h3>
          <div className="cs-canvas-wrap">
            <canvas ref={canvasRef} />
            <div className="cs-sig-actions">
              <button type="button" className="cs-action-btn" onClick={undoStroke}>Undo</button>
              <button type="button" className="cs-action-btn cs-action-clear" onClick={clearSignature}>Clear</button>
            </div>
          </div>
          <p className="cs-hint">Draw your signature above</p>
        </div>

        {/* Agreement Checkbox */}
        <label className="cs-agree">
          <input type="checkbox" checked={agreedTerms} onChange={e => setAgreedTerms(e.target.checked)} />
          I have read and agree to the terms of this Digital Signage Agreement
        </label>

        {/* Submit */}
        <button className="cs-submit" onClick={handleSubmit} disabled={submitting || !agreedTerms || !signeeName.trim()}>
          {submitting ? 'Submitting...' : 'Sign & Submit Agreement'}
        </button>

        <p className="cs-footer">
          Digntec LLC · 15 Veterans Way, Edgewater, NJ 07020 · support@digntec.com
        </p>
      </div>
    </div>
  );
}

export default ContractSign;
